import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { login as loginApi } from "../services/authService";
import { DEFAULT_PATH } from "../routes/routeConfig";
import { toast } from "../utils/toast";
import "../styles/login.css";

const LOGIN_COOLDOWN_STORAGE_KEY = "loginCooldownUntil";

// ── Icons ──────────────────────────────────────────────────────
const EyeOpenIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

// ── Particle canvas hook ───────────────────────────────────────
function useParticles(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    let animId;
    let mouseX = -9999;
    let mouseY = -9999;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();

    const PARTICLE_COUNT = 135;
    const CONNECTION_DIST = 130;
    const REPULSE_DIST = 110;
    const DRIFT_SPEED = 0.018; // constant wander force applied every frame

    const particles = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      radius: Math.random() * 1.8 + 0.8,
      opacity: Math.random() * 0.45 + 0.15,
      // wander: each particle has a slowly rotating drift angle
      angle: Math.random() * Math.PI * 2,
      angleSpeed: (Math.random() - 0.5) * 0.012,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update + draw particles
      for (const p of particles) {
        // Continuous wander — rotate angle slightly each frame then nudge velocity
        p.angle += p.angleSpeed;
        p.vx += Math.cos(p.angle) * DRIFT_SPEED;
        p.vy += Math.sin(p.angle) * DRIFT_SPEED;

        // Mouse repulsion
        const dx = p.x - mouseX;
        const dy = p.y - mouseY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < REPULSE_DIST && dist > 0) {
          const force = ((REPULSE_DIST - dist) / REPULSE_DIST) * 0.6;
          p.vx += (dx / dist) * force;
          p.vy += (dy / dist) * force;
        }

        // Speed cap + friction
        const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        if (speed > 2.5) { p.vx = (p.vx / speed) * 2.5; p.vy = (p.vy / speed) * 2.5; }
        p.vx *= 0.97;
        p.vy *= 0.97;

        p.x += p.vx;
        p.y += p.vy;

        // Wrap edges
        if (p.x < 0) p.x = canvas.width;
        else if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        else if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(176, 198, 255, ${p.opacity})`;
        ctx.fill();
      }

      // Draw connection lines between nearby particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(176, 198, 255, ${(1 - dist / CONNECTION_DIST) * 0.18})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }

      animId = requestAnimationFrame(draw);
    };

    draw();

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
    };
    const onMouseLeave = () => { mouseX = -9999; mouseY = -9999; };
    const onResize = () => { resize(); };

    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseleave", onMouseLeave);
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(animId);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("resize", onResize);
    };
  }, [canvasRef]);
}

// ── Component ──────────────────────────────────────────────────
const Login = ({ onLogin }) => {
  const location = useLocation();
  const canvasRef = useRef(null);

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const isLocked = cooldownSeconds > 0;

  useParticles(canvasRef);

  useEffect(() => {
    try {
      const stored = Number(localStorage.getItem(LOGIN_COOLDOWN_STORAGE_KEY));
      if (Number.isFinite(stored) && stored > Date.now()) setCooldownUntil(stored);
    } catch {}
  }, []);

  useEffect(() => {
    if (!cooldownUntil) {
      setCooldownSeconds(0);
      try { localStorage.removeItem(LOGIN_COOLDOWN_STORAGE_KEY); } catch {}
      return;
    }
    const updateRemaining = () => {
      const remaining = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
      setCooldownSeconds(remaining);
      if (remaining <= 0) {
        setCooldownUntil(null);
        try { localStorage.removeItem(LOGIN_COOLDOWN_STORAGE_KEY); } catch {}
      }
    };
    updateRemaining();
    const timer = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(timer);
  }, [cooldownUntil]);

  const formatCooldown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLocked || isSubmitting) return;
    if (!formData.email || !formData.password) {
      toast.error("Email and password are required.");
      return;
    }
    try {
      setIsSubmitting(true);
      const res = await loginApi(formData.email, formData.password);
      setCooldownUntil(null);
      const redirectTo = location?.state?.from?.pathname || DEFAULT_PATH;
      onLogin({ user: res.user }, redirectTo);
    } catch (err) {
      if (Number(err?.status) === 429) {
        const retryAfter = Number(err?.retryAfter) || 60;
        const nextCooldownUntil = Date.now() + retryAfter * 1000;
        setCooldownUntil(nextCooldownUntil);
        try { localStorage.setItem(LOGIN_COOLDOWN_STORAGE_KEY, String(nextCooldownUntil)); } catch {}
        toast.error(`Too many attempts. Please wait ${retryAfter}s before trying again.`);
      } else {
        toast.error(err?.message || "Invalid credentials.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-split">

      {/* ── Left brand panel ── */}
      <div className="login-brand-panel">
        <canvas ref={canvasRef} className="login-particles-canvas" />
        <div className="login-brand-overlay" aria-hidden="true" />
        <div className="login-brand-content">
          <span className="login-brand-logo">3D.DET</span>
          <p className="login-brand-tagline">
            Advanced content management platform.
          </p>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="login-form-panel">
        <div className="login-form-inner">

          <div className="login-header">
            <h2 className="login-title">Sign in to your account</h2>
            <p className="login-subtitle">Welcome back to the 3D.DET Portal.</p>
          </div>

          <form className="login-form" onSubmit={handleSubmit} noValidate>
            <div className="login-fields">
              <div className="login-field">
                <label htmlFor="email" className="login-label">Email Address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="login-input"
                  placeholder="name@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isLocked || isSubmitting}
                />
              </div>

              <div className="login-field">
                <label htmlFor="password" className="login-label">Password</label>
                <div className="login-password-wrapper">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    className="login-input"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={isLocked || isSubmitting}
                  />
                  <button
                    type="button"
                    className="login-eye-btn"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    disabled={isSubmitting}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeOpenIcon />}
                  </button>
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="login-submit"
              disabled={isLocked || isSubmitting}
            >
              <span>
                {isLocked
                  ? `Try again in ${formatCooldown(cooldownSeconds)}`
                  : isSubmitting
                  ? "Signing in…"
                  : "Sign in"}
              </span>
              {!isLocked && !isSubmitting && <ArrowRightIcon />}
            </button>
          </form>
        </div>
      </div>

    </div>
  );
};

export default Login;
